import { computerMcqChapterByUnit } from "~/features/computer/data/mcq-registry";
import type { ComputerMcqUnit } from "~/features/computer/types/computer";

export type ChapterHubPriorityBlock = {
  label: "High Priority" | "Medium" | "Low";
  items: string[];
};

export type ChapterHubPracticeCard = {
  title: string;
  to: string;
  description: string;
};

export type ChapterHubInteractiveCard = {
  title: string;
  to: string;
  description: string;
};

export type ChapterHubConfig = {
  unit: ComputerMcqUnit;
  chapterNumber: number;
  chapterName: string;
  heroTitle: string;
  shortcutItems: string[];
  examWeight: string[];
  heroNote: string;
  priorityBlocks: ChapterHubPriorityBlock[];
  keyTopicsConceptTo: string;
  practiceCards: ChapterHubPracticeCard[];
  interactiveCards?: ChapterHubInteractiveCard[];
  seoText: string;
};

const sharedRelatedLinks = [
  { title: "Pairing Scheme", to: "/11th/computer/pairing-scheme/", description: "Kis chapter se kya aata hai" },
  { title: "Important Questions", to: "/11th/computer/important-questions/", description: "Top exam questions" },
  { title: "Top 100 MCQs", to: "/11th/computer/mcqs/top-100/", description: "Full practice" },
];

export const chapterHubConfigs: ChapterHubConfig[] = [
  {
    unit: "sdlc",
    chapterNumber: 1,
    chapterName: "Software Development (SDLC)",
    heroTitle: "Chapter 1: Software Development (SDLC) - 11th Computer",
    shortcutItems: [
      "SDLC phases (most important)",
      "Waterfall vs Agile (repeat question)",
      "Functional vs non-functional requirements",
    ],
    examWeight: ["1 Long Question", "2-3 Short Questions", "2-3 MCQs"],
    heroNote: "Yeh chapter ignore nahi kar sakte - basics yahin se aate hain",
    priorityBlocks: [
      { label: "High Priority", items: ["SDLC phases (definition + diagram)", "Waterfall vs Agile comparison"] },
      { label: "Medium", items: ["UML diagrams", "Software testing types"] },
      { label: "Low", items: ["Theory details", "Long explanations"] },
    ],
    keyTopicsConceptTo: "#what-to-prepare",
    practiceCards: [
      { title: "MCQs", to: "/11th/computer/sdlc/mcqs/", description: "Practice chapter-wise MCQs with answers" },
      { title: "Short Questions", to: "/11th/computer/sdlc/short-questions/", description: "Important short questions jo exam mein aate hain" },
      { title: "Long Questions", to: "/11th/computer/sdlc/long-questions/", description: "Most expected long questions (high probability)" },
      { title: "Quick Exercise", to: "/11th/computer/sdlc/exercise/", description: "Basic concept practice" },
    ],
    seoText: "Yeh Chapter 1 SDLC 11th class computer ka important chapter hai jahan se har saal long aur short questions aate hain. Is page par aapko SDLC ke important topics, MCQs, short aur long questions mil jayenge jo exam preparation ke liye kaafi hain.",
  },
  {
    unit: "python",
    chapterNumber: 2,
    chapterName: "Python",
    heroTitle: "Chapter 2: Python - 11th Computer",
    shortcutItems: [
      "Loops aur conditions",
      "Functions with parameters",
      "Input output aur data types",
    ],
    examWeight: ["1 Long Question", "2-3 Short Questions", "1-2 MCQs"],
    heroNote: "Yeh sab se important chapter hai - scoring aur paper control dono yahin se aata hai",
    priorityBlocks: [
      { label: "High Priority", items: ["Loops", "Functions", "Conditions"] },
      { label: "Medium", items: ["Operators and expressions", "Input output"] },
      { label: "Low", items: ["Theory wording", "Minor syntax details"] },
    ],
    keyTopicsConceptTo: "/11th/computer/python/notes/",
    practiceCards: [
      { title: "MCQs", to: "/11th/computer/python/mcqs/", description: "Practice chapter-wise MCQs with answers" },
      { title: "Short Questions", to: "/11th/computer/python/short-questions/", description: "Important short questions jo exam mein aate hain" },
      { title: "Long Questions", to: "/11th/computer/python/long-questions/", description: "Most expected long questions (high probability)" },
      { title: "Quick Exercise", to: "/11th/computer/python/exercise/", description: "Basic concept practice" },
    ],
    interactiveCards: [
      { title: "Variables", to: "/11th/computer/python/variables/", description: "Variable naming, assignment aur basic examples yahan se prepare karo." },
      { title: "Data Types", to: "/11th/computer/python/data-types/", description: "int, float, string aur boolean ko examples ke sath yahan se samjho." },
      { title: "Operators", to: "/11th/computer/python/operators/", description: "Arithmetic aur comparison operators ko exam style me yahan se cover karo." },
      { title: "Input / Output", to: "/11th/computer/python/programs/#input-output-playground", description: "Input aur print output ko visual practice ke sath yahan se samjho." },
      { title: "Loops", to: "/11th/computer/python/loops/", description: "For loop, while loop aur repeat questions ko yahan se cover karo." },
      { title: "Comments", to: "/11th/computer/python/comments/", description: "Comments ka use aur exam-relevant basics yahan se samjho." },
      { title: "Functions", to: "/11th/computer/python/functions/", description: "Functions, parameters aur return concept ko yahan se prepare karo." },
      { title: "Programs", to: "/11th/computer/python/programs/", description: "Board-style code aur output questions yahan se practice karo." },
    ],
    seoText: "Yeh Chapter 2 Python 11th class computer ka sab se important chapter hai jahan se long questions, short questions aur practical style prompts aate hain. Is page par aapko Python ke important topics, MCQs, short aur long questions mil jayenge jo exam preparation ke liye kaafi hain.",
  },
  {
    unit: "algorithm",
    chapterNumber: 3,
    chapterName: "Algorithms",
    heroTitle: "Chapter 3: Algorithms - 11th Computer",
    shortcutItems: [
      "Flowcharts and symbols",
      "Pseudocode and algorithms",
      "Searching and sorting basics",
    ],
    examWeight: ["1 Long Question", "2-3 Short Questions", "1-2 MCQs"],
    heroNote: "Yeh chapter strong ho to paper ka logical section bohat easy ho jata hai",
    priorityBlocks: [
      { label: "High Priority", items: ["Flowcharts", "Pseudocode", "Problem solving steps"] },
      { label: "Medium", items: ["Linear search", "Bubble sort", "Time complexity"] },
      { label: "Low", items: ["Extra theory lines", "Advanced details"] },
    ],
    keyTopicsConceptTo: "#what-to-prepare",
    practiceCards: [
      { title: "MCQs", to: "/11th/computer/algorithm/mcqs/", description: "Practice chapter-wise MCQs with answers" },
      { title: "Short Questions", to: "/11th/computer/algorithm/short-questions/", description: "Important short questions jo exam mein aate hain" },
      { title: "Long Questions", to: "/11th/computer/algorithm/long-questions/", description: "Most expected long questions (high probability)" },
      { title: "Quick Exercise", to: "/11th/computer/algorithm/exercise/", description: "Basic concept practice" },
    ],
    seoText: "Yeh Chapter 3 Algorithms 11th class computer ka high priority chapter hai jahan se long aur short questions regular aate hain. Is page par aapko algorithms ke important topics, MCQs, short aur long questions mil jayenge jo exam preparation ke liye kaafi hain.",
  },
  {
    unit: "data-structures",
    chapterNumber: 4,
    chapterName: "Data Structures",
    heroTitle: "Chapter 4: Data Structures - 11th Computer",
    shortcutItems: [
      "Lists, stacks aur queues",
      "Trees aur graphs basics",
      "Operations and definitions",
    ],
    examWeight: ["1 Long Question", "2 Short Questions", "2 MCQs"],
    heroNote: "Yeh support chapter hai lekin achi prep score ko push karti hai",
    priorityBlocks: [
      { label: "High Priority", items: ["Lists", "Stacks vs queues", "Tree basics"] },
      { label: "Medium", items: ["Graphs", "Operations and examples"] },
      { label: "Low", items: ["Long theory", "Extra details"] },
    ],
    keyTopicsConceptTo: "#what-to-prepare",
    practiceCards: [
      { title: "MCQs", to: "/11th/computer/data-structures/mcqs/", description: "Practice chapter-wise MCQs with answers" },
      { title: "Short Questions", to: "/11th/computer/data-structures/short-questions/", description: "Important short questions jo exam mein aate hain" },
      { title: "Long Questions", to: "/11th/computer/data-structures/long-questions/", description: "Most expected long questions (high probability)" },
      { title: "Quick Exercise", to: "/11th/computer/data-structures/exercise/", description: "Basic concept practice" },
    ],
    seoText: "Yeh Chapter 4 Data Structures 11th class computer ka important support chapter hai jahan se short aur long questions dono aate hain. Is page par aapko data structures ke important topics, MCQs, short aur long questions mil jayenge jo exam preparation ke liye kaafi hain.",
  },
  {
    unit: "data-analytics",
    chapterNumber: 5,
    chapterName: "Data Analytics",
    heroTitle: "Chapter 5: Data Analytics - 11th Computer",
    shortcutItems: [
      "Charts aur visualization",
      "Models and prediction basics",
      "Experiments and data trends",
    ],
    examWeight: ["1 Long Question", "2 Short Questions", "1-2 MCQs"],
    heroNote: "Yeh chapter moderate priority ka hai lekin targeted prep se score improve hota hai",
    priorityBlocks: [
      { label: "High Priority", items: ["Visualization", "Linear regression basics"] },
      { label: "Medium", items: ["Experiments", "Clustering basics"] },
      { label: "Low", items: ["Extra theory details", "Advanced terms"] },
    ],
    keyTopicsConceptTo: "#what-to-prepare",
    practiceCards: [
      { title: "MCQs", to: "/11th/computer/data-analytics/mcqs/", description: "Practice chapter-wise MCQs with answers" },
      { title: "Short Questions", to: "/11th/computer/data-analytics/short-questions/", description: "Important short questions jo exam mein aate hain" },
      { title: "Long Questions", to: "/11th/computer/data-analytics/long-questions/", description: "Most expected long questions (high probability)" },
      { title: "Quick Exercise", to: "/11th/computer/data-analytics/exercise/", description: "Basic concept practice" },
    ],
    seoText: "Yeh Chapter 5 Data Analytics 11th class computer ka useful chapter hai jahan se short questions aur selected long questions aate hain. Is page par aapko data analytics ke important topics, MCQs, short aur long questions mil jayenge jo exam preparation ke liye kaafi hain.",
  },
  {
    unit: "emerging-tech",
    chapterNumber: 6,
    chapterName: "Emerging Technologies",
    heroTitle: "Chapter 6: Emerging Technologies - 11th Computer",
    shortcutItems: [
      "Cloud service models",
      "Blockchain basics",
      "Deployment and serverless concepts",
    ],
    examWeight: ["1 Long Question", "2 Short Questions", "1-2 MCQs"],
    heroNote: "Yeh chapter selective prep se achi coverage deta hai",
    priorityBlocks: [
      { label: "High Priority", items: ["Cloud models", "Blockchain basics"] },
      { label: "Medium", items: ["Serverless", "Deployment models"] },
      { label: "Low", items: ["Extra examples", "Long theory"] },
    ],
    keyTopicsConceptTo: "#what-to-prepare",
    practiceCards: [
      { title: "MCQs", to: "/11th/computer/emerging-tech/mcqs/", description: "Practice chapter-wise MCQs with answers" },
      { title: "Short Questions", to: "/11th/computer/emerging-tech/short-questions/", description: "Important short questions jo exam mein aate hain" },
      { title: "Long Questions", to: "/11th/computer/emerging-tech/long-questions/", description: "Most expected long questions (high probability)" },
      { title: "Quick Exercise", to: "/11th/computer/emerging-tech/exercise/", description: "Basic concept practice" },
    ],
    seoText: "Yeh Chapter 6 Emerging Technologies 11th class computer ka selective chapter hai jahan se short aur long questions dono aasakte hain. Is page par aapko emerging technologies ke important topics, MCQs, short aur long questions mil jayenge jo exam preparation ke liye kaafi hain.",
  },
  {
    unit: "legal-ethics",
    chapterNumber: 7,
    chapterName: "Legal & Ethical Aspects",
    heroTitle: "Chapter 7: Legal & Ethical Aspects - 11th Computer",
    shortcutItems: [
      "Phishing aur digital threats",
      "Privacy policy and terms",
      "Digital divide and responsible use",
    ],
    examWeight: ["Selective Long Question", "1-2 Short Questions", "2 MCQs"],
    heroNote: "Yeh chapter low priority hai lekin MCQs aur short prep ke liye ignore nahi karna chahiye",
    priorityBlocks: [
      { label: "High Priority", items: ["Phishing and pharming", "Privacy policy", "Digital divide"] },
      { label: "Medium", items: ["Terms of use", "Responsible digital behavior"] },
      { label: "Low", items: ["Long explanations", "Extra legal wording"] },
    ],
    keyTopicsConceptTo: "#what-to-prepare",
    practiceCards: [
      { title: "MCQs", to: "/11th/computer/legal-ethics/mcqs/", description: "Practice chapter-wise MCQs with answers" },
      { title: "Short Questions", to: "/11th/computer/legal-ethics/short-questions/", description: "Important short questions jo exam mein aate hain" },
      { title: "Long Questions", to: "/11th/computer/legal-ethics/long-questions/", description: "Most expected long questions (high probability)" },
      { title: "Quick Exercise", to: "/11th/computer/legal-ethics/exercise/", description: "Basic concept practice" },
    ],
    seoText: "Yeh Chapter 7 Legal and Ethical Aspects 11th class computer ka lower priority chapter hai lekin MCQs aur short questions ke liye important hai. Is page par aapko legal and ethical aspects ke important topics, MCQs, short aur long questions mil jayenge jo exam preparation ke liye kaafi hain.",
  },
  {
    unit: "digital-literacy",
    chapterNumber: 8,
    chapterName: "Digital Literacy",
    heroTitle: "Chapter 8: Digital Literacy - 11th Computer",
    shortcutItems: [
      "Boolean operators",
      "Reliable sources and evaluation",
      "Academic search and copyright",
    ],
    examWeight: ["Selective Long Question", "1-2 Short Questions", "2 MCQs"],
    heroNote: "Yeh chapter MCQs aur selective short questions ke liye useful hai",
    priorityBlocks: [
      { label: "High Priority", items: ["Boolean operators", "Reliable source evaluation"] },
      { label: "Medium", items: ["Academic libraries", "Citation and plagiarism"] },
      { label: "Low", items: ["Extra theory lines", "Long details"] },
    ],
    keyTopicsConceptTo: "#what-to-prepare",
    practiceCards: [
      { title: "MCQs", to: "/11th/computer/digital-literacy/mcqs/", description: "Practice chapter-wise MCQs with answers" },
      { title: "Short Questions", to: "/11th/computer/digital-literacy/short-questions/", description: "Important short questions jo exam mein aate hain" },
      { title: "Long Questions", to: "/11th/computer/digital-literacy/long-questions/", description: "Most expected long questions (high probability)" },
      { title: "Quick Exercise", to: "/11th/computer/digital-literacy/exercise/", description: "Basic concept practice" },
    ],
    seoText: "Yeh Chapter 8 Digital Literacy 11th class computer ka selective chapter hai jahan se MCQs aur short questions aate hain. Is page par aapko digital literacy ke important topics, MCQs, short aur long questions mil jayenge jo exam preparation ke liye kaafi hain.",
  },
  {
    unit: "entrepreneurship",
    chapterNumber: 9,
    chapterName: "Entrepreneurship",
    heroTitle: "Chapter 9: Entrepreneurship - 11th Computer",
    shortcutItems: [
      "Design thinking steps",
      "Business plan basics",
      "Marketing and profit concepts",
    ],
    examWeight: ["Selective Long Question", "1-2 Short Questions", "1-2 MCQs"],
    heroNote: "Yeh chapter low priority hai lekin selective prep se easy marks mil sakte hain",
    priorityBlocks: [
      { label: "High Priority", items: ["Design thinking", "Business plan basics"] },
      { label: "Medium", items: ["Marketing strategy", "Profit and revenue"] },
      { label: "Low", items: ["Extra examples", "Long explanations"] },
    ],
    keyTopicsConceptTo: "#what-to-prepare",
    practiceCards: [
      { title: "MCQs", to: "/11th/computer/entrepreneurship/mcqs/", description: "Practice chapter-wise MCQs with answers" },
      { title: "Short Questions", to: "/11th/computer/entrepreneurship/short-questions/", description: "Important short questions jo exam mein aate hain" },
      { title: "Long Questions", to: "/11th/computer/entrepreneurship/long-questions/", description: "Most expected long questions (high probability)" },
      { title: "Quick Exercise", to: "/11th/computer/entrepreneurship/exercise/", description: "Basic concept practice" },
    ],
    seoText: "Yeh Chapter 9 Entrepreneurship 11th class computer ka lower priority chapter hai lekin short aur MCQs ke liye useful hai. Is page par aapko entrepreneurship ke important topics, MCQs, short aur long questions mil jayenge jo exam preparation ke liye kaafi hain.",
  },
];

export const chapterHubByUnit = Object.fromEntries(
  chapterHubConfigs.map((item) => [item.unit, item]),
) as Record<ComputerMcqUnit, ChapterHubConfig>;

export const chapterHubRelatedLinks = sharedRelatedLinks;

export function getChapterHubFaqs(unit: ComputerMcqUnit) {
  const config = chapterHubByUnit[unit];
  const mcqChapter = computerMcqChapterByUnit[unit];
  const shortQuestionsLink = config.practiceCards.find((item) => item.title === "Short Questions")?.to ?? mcqChapter.routes.chapterTo;
  const longQuestionsLink = config.practiceCards.find((item) => item.title === "Long Questions")?.to ?? mcqChapter.routes.chapterTo;

  return [
    {
      question: `Chapter ${config.chapterNumber} ${config.chapterName} kya important hai?`,
      answer: `${config.chapterName} 11th class computer ka important chapter hai. ${config.heroNote}.`,
    },
    {
      question: `Chapter ${config.chapterNumber} ${config.chapterName} me kya prepare karein?`,
      answer: `Is chapter me sab se pehle ${config.priorityBlocks[0]?.items.join(", ")} prepare karein. Yehi topics exam me sab se zyada help karte hain.`,
    },
    {
      question: `Chapter ${config.chapterNumber} ${config.chapterName} ke MCQs kahan milte hain?`,
      answer: `Is chapter ke MCQs aap ${mcqChapter.routes.mcqTo} par practice kar sakte hain jahan chapter-wise objective questions answers ke sath milte hain.`,
    },
    {
      question: `Chapter ${config.chapterNumber} ${config.chapterName} ke short aur long questions kahan milte hain?`,
      answer: `Short questions ${shortQuestionsLink} par aur long questions ${longQuestionsLink} par milte hain. Dono exam preparation ke liye targeted form me diye gaye hain.`,
    },
  ];
}

export function getChapterHubSchemaLinks(unit: ComputerMcqUnit) {
  const config = chapterHubByUnit[unit];
  const mcqChapter = computerMcqChapterByUnit[unit];

  return [
    { name: `${config.chapterName} Concepts`, path: mcqChapter.routes.chapterTo },
    { name: `${config.chapterName} MCQs`, path: mcqChapter.routes.mcqTo },
    { name: `${config.chapterName} Short Questions`, path: config.practiceCards[1].to },
    { name: `${config.chapterName} Long Questions`, path: config.practiceCards[2].to },
    { name: `${config.chapterName} Exercise`, path: config.practiceCards[3].to },
    ...(config.interactiveCards?.map((item) => ({ name: item.title, path: item.to })) ?? []),
    { name: "Pairing Scheme", path: "/11th/computer/pairing-scheme/" },
    { name: "Important Questions", path: "/11th/computer/important-questions/" },
    { name: "Top 100 MCQs", path: "/11th/computer/mcqs/top-100/" },
  ];
}
